/**
 * Shared href validation for block-config CTAs and links.
 *
 * Config-driven hrefs render into `<Link href>` on the public storefront, so
 * "any non-empty string" would let a content editor store a `javascript:` /
 * `data:` URL — stored XSS from brand staff to visitors (React only warns on
 * `javascript:` hrefs, it does not block them). Allowlist instead: a
 * site-relative path (`/…`, not scheme-relative `//…`) or an absolute
 * http(s) URL. A pre-existing row that fails this re-validates on read and is
 * dropped by the composition fail-safe — never rendered unsafely.
 */
import { z } from "zod";

export function isSafeHref(href: string): boolean {
  if (href.startsWith("/")) return !href.startsWith("//");
  try {
    const protocol = new URL(href).protocol;
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
}

export const safeHrefSchema = z
  .string()
  .min(1)
  .max(2048)
  .refine(isSafeHref, {
    message: "Must be a site-relative path (/…) or an http(s) URL",
  });
